import React, { useState, useEffect } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import {
  DollarSign, TrendingUp, TrendingDown, AlertCircle, Clock,
  CheckCircle, CreditCard, ArrowUpRight, ArrowDownRight, Users,
  FileText, Search, Plus, X
} from "lucide-react";

const FinancialTab = ({ clients = [], onOpenClient }) => {
  const [summary, setSummary] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("overview"); // overview, clients, invoices
  const [searchTerm, setSearchTerm] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ invoice_id: "", amount: "", method: "cash", notes: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sumRes, balRes] = await Promise.all([
        axios.get(`${API}/financial/summary`, { withCredentials: true }),
        axios.get(`${API}/financial/client-balances`, { withCredentials: true })
      ]);
      setSummary(sumRes.data);
      setBalances(balRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/payments/mark-paid`, { invoice_id: invoiceId, payment_method: "manual" }, { withCredentials: true });
      toast.success("Invoice marked as paid");
      fetchData();
    } catch (e) { toast.error("Failed to update invoice"); }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.invoice_id || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Select invoice and enter valid amount");
      return;
    }
    try {
      await axios.post(`${API}/payments/add-manual`, {
        invoice_id: paymentForm.invoice_id,
        amount: parseFloat(paymentForm.amount),
        method: paymentForm.method,
        notes: paymentForm.notes
      }, { withCredentials: true });
      toast.success("Payment recorded");
      setShowPaymentModal(false);
      setPaymentForm({ invoice_id: "", amount: "", method: "cash", notes: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record payment"); }
  };

  const filteredBalances = balances.filter(b => {
    if (!searchTerm) return true;
    return b.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           b.client_email?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-tab">
      {/* View Toggle */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: "overview", label: "Overview" },
          { id: "clients", label: "Client Balances" },
          { id: "invoices", label: "Unpaid Invoices" }
        ].map(v => (
          <button key={v.id} onClick={() => setViewMode(v.id)} data-testid={`fin-view-${v.id}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === v.id ? "bg-[#FF6B35] text-white" : "bg-[#15161C] text-[#A0AAB2] border border-[#2A2B32]"}`}>
            {v.label}
          </button>
        ))}
        <button onClick={() => setShowPaymentModal(true)} data-testid="add-payment-btn"
          className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/30 ml-auto">
          <Plus size={16} /><span>Add Payment</span>
        </button>
      </div>

      {/* Overview Cards */}
      {viewMode === "overview" && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-5">
              <div className="flex items-center space-x-2 mb-2">
                <div className="p-2 bg-green-500/20 rounded-lg"><TrendingUp size={18} className="text-green-400" /></div>
                <span className="text-[#A0AAB2] text-sm">Revenue</span>
              </div>
              <p className="text-2xl font-bold text-green-400" data-testid="total-revenue">${summary.total_revenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              <p className="text-[#A0AAB2] text-xs mt-1">{summary.paid_count} paid invoices</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-5">
              <div className="flex items-center space-x-2 mb-2">
                <div className="p-2 bg-yellow-500/20 rounded-lg"><Clock size={18} className="text-yellow-400" /></div>
                <span className="text-[#A0AAB2] text-sm">Outstanding</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400" data-testid="total-outstanding">${summary.total_outstanding.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              <p className="text-[#A0AAB2] text-xs mt-1">{summary.unpaid_count} unpaid invoices</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-5">
              <div className="flex items-center space-x-2 mb-2">
                <div className="p-2 bg-red-500/20 rounded-lg"><AlertCircle size={18} className="text-red-400" /></div>
                <span className="text-[#A0AAB2] text-sm">Overdue</span>
              </div>
              <p className="text-2xl font-bold text-red-400" data-testid="total-overdue">${summary.total_overdue.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              <p className="text-[#A0AAB2] text-xs mt-1">{summary.overdue_count} overdue</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-5">
              <div className="flex items-center space-x-2 mb-2">
                <div className="p-2 bg-[#FF6B35]/20 rounded-lg"><DollarSign size={18} className="text-[#FF6B35]" /></div>
                <span className="text-[#A0AAB2] text-sm">Profit</span>
              </div>
              <p className="text-2xl font-bold text-[#FF6B35]" data-testid="total-profit">${summary.total_profit.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              <p className="text-[#A0AAB2] text-xs mt-1">After ${summary.total_expenses.toLocaleString()} expenses</p>
            </div>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <span className="text-[#A0AAB2] text-xs">Total Invoiced</span>
              <p className="text-xl font-bold text-white">${summary.total_invoiced.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <span className="text-[#A0AAB2] text-xs">Total Quoted</span>
              <p className="text-xl font-bold text-white">${summary.total_quoted.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <span className="text-[#A0AAB2] text-xs">Collection Rate</span>
              <p className="text-xl font-bold text-white">
                {summary.total_invoiced > 0 ? Math.round(summary.total_revenue / summary.total_invoiced * 100) : 0}%
              </p>
            </div>
          </div>

          {/* Recent Payments */}
          {summary.recent_payments?.length > 0 && (
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h3 className="text-base font-semibold text-white mb-4">Recent Payments</h3>
              <div className="space-y-2">
                {summary.recent_payments.map(inv => (
                  <div key={inv.id} onClick={() => onOpenClient && inv.client_id && onOpenClient(inv.client_id)}
                    className={`flex items-center justify-between bg-[#1F2027] rounded-lg p-3 ${inv.client_id ? "cursor-pointer hover:bg-[#2A2B32] transition-all" : ""}`}
                    data-testid={`recent-payment-${inv.id}`}>
                    <div>
                      <span className="text-white text-sm font-medium">{inv.client_name}</span>
                      <span className="text-[#A0AAB2] text-xs ml-2">{inv.invoice_number}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-green-400 font-bold">${inv.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                      <span className="text-[#A0AAB2] text-xs">{inv.payment_date ? new Date(inv.payment_date).toLocaleDateString() : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Client Balances View */}
      {viewMode === "clients" && (
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white">Client Balances</h3>
            <div className="relative w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search clients..." data-testid="fin-search-clients"
                className="w-full pl-9 pr-4 py-2 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm" />
            </div>
          </div>
          {filteredBalances.length === 0 ? (
            <p className="text-[#A0AAB2] text-center py-8">No client data found</p>
          ) : (
            <div className="space-y-2">
              {filteredBalances.map(b => (
                <div key={b.client_id}
                  onClick={() => onOpenClient && onOpenClient(b.client_id)}
                  className={`bg-[#1F2027] border rounded-lg p-4 cursor-pointer hover:border-[#FF6B35]/50 transition-all ${b.overdue > 0 ? "border-red-500/30" : b.outstanding > 0 ? "border-yellow-500/30" : "border-[#2A2B32]"}`}
                  data-testid={`client-balance-${b.client_id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-white font-semibold">{b.client_name}</h4>
                        {b.overdue > 0 && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">OVERDUE</span>}
                        {b.outstanding > 0 && b.overdue === 0 && <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">Owes Money</span>}
                        {b.outstanding === 0 && b.total_invoiced > 0 && <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Paid Up</span>}
                      </div>
                      <p className="text-[#A0AAB2] text-sm">{b.client_email} | {b.client_phone}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-right">
                      <div>
                        <span className="text-[#A0AAB2] text-xs">Invoiced</span>
                        <p className="text-white font-medium">${b.total_invoiced.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div>
                        <span className="text-[#A0AAB2] text-xs">Paid</span>
                        <p className="text-green-400 font-medium">${b.total_paid.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div>
                        <span className="text-[#A0AAB2] text-xs">Owes</span>
                        <p className={`font-bold ${b.outstanding > 0 ? "text-yellow-400" : "text-green-400"}`}>
                          ${b.outstanding.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unpaid Invoices View */}
      {viewMode === "invoices" && summary && (
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
          <h3 className="text-base font-semibold text-white mb-4">Unpaid Invoices ({summary.unpaid_invoices?.length || 0})</h3>
          {(!summary.unpaid_invoices || summary.unpaid_invoices.length === 0) ? (
            <p className="text-[#A0AAB2] text-center py-8">All invoices are paid!</p>
          ) : (
            <div className="space-y-2">
              {summary.unpaid_invoices.map(inv => {
                const today = new Date().toISOString().split("T")[0];
                const isOverdue = inv.due_date && inv.due_date < today;
                const isDueToday = inv.due_date === today;
                const flagStyle = isOverdue ? { bg: "bg-red-500/20", text: "text-red-400", label: "OVERDUE", border: "border-red-500/30" }
                  : isDueToday ? { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "DUE TODAY", border: "border-yellow-500/30" }
                  : { bg: "bg-blue-500/20", text: "text-blue-400", label: "UPCOMING", border: "border-[#2A2B32]" };
                return (
                  <div key={inv.id} onClick={(e) => { if (e.target.closest('button')) return; onOpenClient && inv.client_id && onOpenClient(inv.client_id); }}
                    className={`bg-[#1F2027] border rounded-lg p-4 ${flagStyle.border} ${inv.client_id ? "cursor-pointer hover:border-[#FF6B35]/50 transition-all" : ""}`}
                    data-testid={`unpaid-invoice-${inv.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-white font-medium">{inv.invoice_number}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${flagStyle.bg} ${flagStyle.text}`}>{flagStyle.label}</span>
                        </div>
                        <p className="text-[#A0AAB2] text-sm">{inv.client_name}</p>
                        <p className="text-[#A0AAB2] text-xs">Due: {inv.due_date}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <p className={`font-bold text-lg ${flagStyle.text}`}>${inv.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                        <button onClick={() => handleMarkPaid(inv.id)} data-testid={`mark-paid-${inv.id}`}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30">
                          <CheckCircle size={14} /><span>Mark Paid</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="payment-modal">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Record Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Invoice *</label>
                <select value={paymentForm.invoice_id} onChange={e => setPaymentForm(p => ({...p, invoice_id: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="payment-invoice">
                  <option value="">Select invoice</option>
                  {summary?.unpaid_invoices?.map(inv => (
                    <option key={inv.id} value={inv.id}>{inv.invoice_number} - {inv.client_name} (${inv.amount})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Amount *</label>
                <input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({...p, amount: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="0.00" data-testid="payment-amount" />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Payment Method</label>
                <select value={paymentForm.method} onChange={e => setPaymentForm(p => ({...p, method: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="payment-method">
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="ach">ACH / Bank Transfer</option>
                  <option value="check">Check</option>
                  <option value="zelle">Zelle</option>
                </select>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Notes</label>
                <input type="text" value={paymentForm.notes} onChange={e => setPaymentForm(p => ({...p, notes: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Optional note" data-testid="payment-notes" />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button onClick={() => setShowPaymentModal(false)} className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg">Cancel</button>
              <button onClick={handleAddPayment} className="flex-1 py-3 gradient-btn rounded-lg font-semibold" data-testid="submit-payment-btn">Record Payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialTab;
